import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import * as Sentry from "@sentry/react";

interface RealtimeUpdateOptions {
  table: string;
  events?: ('INSERT' | 'UPDATE' | 'DELETE')[];
  filter?: string;
  onUpdate?: (payload: any) => void;
  invalidateQueries?: string[];
}

export function useRealtimeUpdates({
  table,
  events = ['INSERT', 'UPDATE', 'DELETE'],
  filter,
  onUpdate,
  invalidateQueries = []
}: RealtimeUpdateOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  // Toast deduplication to prevent spam from rapid events
  const recentToasts = useRef<Set<string>>(new Set());

  const handleRealtimeUpdate = useCallback((payload: any) => {
    // Invalidate relevant queries
    invalidateQueries.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    });

    // Custom update handler
    if (onUpdate) {
      onUpdate(payload);
    }
  }, [table, queryClient, onUpdate, invalidateQueries]);
  
  /**
   * Show toast with deduplication to prevent spam
   * Prevents showing the same toast type within 3 seconds
   */
  const showToast = useCallback((type: string, message: string, title?: string) => {
    const toastKey = `${type}-${table}`;
    
    // Check if we recently showed this toast
    if (!recentToasts.current.has(toastKey)) {
      toast({
        title: title || 'Update',
        description: message,
      });
      
      // Mark as shown
      recentToasts.current.add(toastKey);
      
      // Clear from cache after 3 seconds
      setTimeout(() => {
        recentToasts.current.delete(toastKey);
      }, 3000);
    }
  }, [table, toast]);

  useEffect(() => {
    if (!user) return;

    let channel: any;
    
    const setupRealtimeSubscription = () => {
      // Create channel with user-specific or table-specific name
      const channelName = filter ? `${table}_${filter}` : `${table}_changes`;
      
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events, then filter
            schema: 'public',
            table: table,
            filter: filter
          },
          (payload) => {
            // Only process events we're interested in
            if (events.includes(payload.eventType as any)) {
              handleRealtimeUpdate(payload);
            }
          }
        )
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            setIsConnected(true);
            setConnectionError(null);
          } else if (status === 'CHANNEL_ERROR') {
            Sentry.captureException(new Error(`Realtime subscription error for ${table}`), {
              tags: { context: 'realtime_error', operation: 'realtime_updates', action: 'channel_error' },
              extra: { message: `Realtime subscription error for ${table}`, status, table }
            });
            setIsConnected(false);
            setConnectionError('Failed to connect to realtime updates');
          } else if (status === 'TIMED_OUT') {
            Sentry.captureException(new Error(`Realtime subscription timeout for ${table}`), {
              tags: { context: 'realtime_error', operation: 'realtime_updates', action: 'timeout' },
              extra: { message: `Realtime subscription timeout for ${table}`, status, table }
            });
            setIsConnected(false);
            setConnectionError('Realtime connection timed out');
          }
        });
    };

    // Setup subscription
    setupRealtimeSubscription();

    // Cleanup
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, table, events, filter, handleRealtimeUpdate]);

  // Retry connection
  const retryConnection = useCallback(() => {
    setConnectionError(null);
    // The useEffect will handle reconnection when connectionError changes
  }, []);

  return {
    isConnected,
    connectionError,
    retryConnection,
    showToast, // Export toast with deduplication
  };
}

// Specialized hooks for common use cases
export function useAssessmentRealtimeUpdates(userId?: string) {
  return useRealtimeUpdates({
    table: 'assessment_progress',
    events: ['INSERT', 'UPDATE'],
    filter: userId ? `user_id=eq.${userId}` : undefined,
    invalidateQueries: ['assessment-progress', 'user-assessments'],
    onUpdate: () => {
      // Assessment progress updated
    }
  });
}

export function usePreferencesRealtimeUpdates(userId?: string) {
  return useRealtimeUpdates({
    table: 'user_preferences',
    events: ['INSERT', 'UPDATE'],
    filter: userId ? `user_id=eq.${userId}` : undefined,
    invalidateQueries: ['user-preferences'],
    onUpdate: () => {
      // User preferences updated
    }
  });
}

export function useClientsRealtimeUpdates(organizationId?: string) {
  return useRealtimeUpdates({
    table: 'clients',
    events: ['INSERT', 'UPDATE', 'DELETE'],
    filter: organizationId ? `organization_id=eq.${organizationId}` : undefined,
    invalidateQueries: ['clients', 'client-summary'],
    onUpdate: () => {
      // Client data updated
    }
  });
}